import { __awaiter } from "tslib";
import { makeAutoObservable, runInAction } from "mobx";
import { nanoid } from "nanoid";
export const REMOTE_COMMENT = "remote";
export const LOCAL_COMMENT = "local";
/**
 * DocumentCommentsManager
 *
 * Manages comments for a document including:
 * - Fetching and monitoring comments from Firestore
 * - Managing a queue of pending comments
 * - Coordinating the order in which comments appear
 *
 */
export class DocumentCommentsManager {
    constructor() {
        Object.defineProperty(this, "comments", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "pendingComments", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "isCheckingPending", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        makeAutoObservable(this, {}, { autoBind: true });
    }
    /**
     * Check pending comments to see if any can be resolved or posted.
     * This is called automatically when comments are updated via Firestore, but can also be called manually.
     */
    checkPendingComments() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isCheckingPending)
                return;
            this.isCheckingPending = true;
            try {
                const toRemove = [];
                const toPost = [];
                for (const pending of this.pendingComments) {
                    if (pending.postingType === REMOTE_COMMENT) {
                        if (pending.checkCompleted(this.comments)) {
                            toRemove.push(pending.id);
                        }
                    }
                    else {
                        // Other comments should be posted after all preceding pending items are resolved.
                        const indexOfThis = this.pendingComments.indexOf(pending);
                        const allBeforeAreResolved = this.pendingComments
                            .slice(0, indexOfThis)
                            .every(p => toRemove.includes(p.id));
                        if (allBeforeAreResolved) {
                            toPost.push(pending);
                            toRemove.push(pending.id);
                        }
                    }
                }
                // Remove resolved items immediately.
                if (toRemove.length > 0) {
                    runInAction(() => {
                        this.pendingComments = this.pendingComments.filter(p => !toRemove.includes(p.id));
                    });
                }
                // Post local comments.
                for (const commentToPost of toPost) {
                    try {
                        yield commentToPost.postFunction({
                            comment: commentToPost.comment,
                            context: commentToPost.context,
                            document: commentToPost.document
                        });
                    }
                    catch (error) {
                        console.error("Failed to post pending local comment:", error);
                    }
                }
            }
            finally {
                this.isCheckingPending = false;
            }
        });
    }
    setComments(comments) {
        this.comments = comments;
        this.checkPendingComments();
    }
    queueRemoteComment({ triggeredAt, source, checkCompleted }) {
        const pending = {
            id: `${source}-comment-${nanoid()}`,
            triggeredAt,
            postingType: REMOTE_COMMENT,
            source,
            checkCompleted
        };
        this.pendingComments.push(pending);
    }
    queueComment({ comment, context, document, source, postFunction }) {
        const pending = {
            id: `local-comment-${nanoid()}`,
            comment,
            context,
            document,
            postingType: LOCAL_COMMENT,
            source,
            postFunction
        };
        this.pendingComments.push(pending);
        this.checkPendingComments();
    }
    dispose() {
        this.comments = [];
        this.pendingComments = [];
    }
}
//# sourceMappingURL=data:application/json;base64,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